import { Injectable } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { filter, firstValueFrom, Subject } from 'rxjs';
import { ProjectConfigService } from './project-config.service';
import { AuthService } from './auth.service';
import { LoadingService } from './loading.service';

@Injectable({
    providedIn: 'root'
})
export class QueryParamsService {
    private token: string | null = null;
    private initialized = false;

    private currentUrl: string = '';
    
    constructor(
        private load: LoadingService,
        private projectConfig: ProjectConfigService,
        private authService: AuthService,
        private router: Router,
    ) {}

    public async initialize(): Promise<void> {
        if (this.initialized) return;
        this.initialized = true;
        
        const { hasIframe } = this.projectConfig.getConfig();
        
        if (hasIframe) {
            this.load.loadingShowAlt();
            this.currentUrl = this.getCurrentUrl();
            return this.checkInitialParams();
        }
        
        // Resuelve inmediatamente si no hay iframe
        return Promise.resolve();
    }

    private async checkInitialParams(): Promise<void> {
        try {
            const url = new URL(window.location.href);
            const params = new URLSearchParams(url.search);
            return this.processParams(params);
        } catch (error) {
            console.error('Error checking initial params:', error);
            this.load.dismissAlt();
            return Promise.resolve();
        }
    }

    private async processParams(params: any): Promise<void> {
        // Verificar bearer_token
        const bearerToken = params.get('bearer_token');
        if (bearerToken) {
            this.token = bearerToken;
            return this.handleBearerToken();
        }
        
        // Para otros casos, resolvemos inmediatamente
        this.load.dismissAlt();
        return Promise.resolve();
    }

    private async handleBearerToken(): Promise<void> {
        if (!this.token) {
            this.load.dismissAlt();
            return Promise.resolve()
        };

        const url = new URL(window.location.href);
        url.searchParams.delete('bearer_token');
        const newUrl = url.pathname + url.search;

        this.authService.saveAuth(
            this.token,
            (undefined),
            (undefined)
        );

        return this.replaceUrlWithoutNavigation(newUrl);
    }

    private replaceUrlWithoutNavigation(newUrl: string): Promise<void> {
        return new Promise((resolve) => {
            try {
                this.router.navigateByUrl(newUrl, {
                    replaceUrl: true,
                    skipLocationChange: false
                });
                this.load.dismissAlt();
                resolve();
            } catch (e) {
                window.history.replaceState(null, '', newUrl);
                resolve();
            }
        });
    }
    
    public getToken(): string | null {
        return this.token;
    }

    private getCurrentUrl(): string {
        // Primero intentar con NavigationEnd si está disponible
        if (this.currentUrl) return this.currentUrl;
        
        // Fallback 1: Usar window.location
        const path = window.location.pathname;
        if (path && path !== '/') return path;
        
        // Fallback 2: Usar Router.url (puede devolver '/' en inicialización)
        return this.router.url.split('?')[0] || '/';
    }
}